import { useState } from "react";
import { flatMap } from "lodash";
import useMonitoredRouteAPI, { RouteData } from "api/MonitoredRouteAPI";
import { useAuth } from "contexts/AuthContext";

export type RouteDirection = {
  route: RouteData;
  path: google.maps.LatLngLiteral[];
  polylines?: google.maps.Polyline[];
};

export type RouteDirections = RouteDirection[];

export const getLatLngFromPosition = (
  position: string
): google.maps.LatLngLiteral => {
  const [latString, lngString] = position.split(",");

  const lat = Number(latString);
  const lng = Number(lngString);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error(`Invalid map position: ${position}`);
  }

  return {
    lat,
    lng
  };
};

const routeIsEqual = (route: RouteData, newRoute: RouteData): boolean =>
  route.route_name === newRoute.route_name &&
  route.initial_camera === newRoute.initial_camera &&
  route.final_camera === newRoute.final_camera;

const routeExists = (route: RouteData, oldRoutes: RouteData[]): boolean => {
  if (!oldRoutes || oldRoutes.length === 0) {
    return false;
  }

  return oldRoutes.some(oldRoute => routeIsEqual(route, oldRoute));
};

const useRouteFlowManager = (): [
  boolean,
  () => Promise<RouteDirections>,
  (r: RouteData[]) => Promise<RouteDirections>,
  (r: RouteDirections) => void
] => {
  const [isBusy, setIsBusy] = useState(false);

  const [routeDirectionsState, setRouteDirectionsState] =
    useState<RouteDirections>([]);

  const { sessionUser } = useAuth();

  const MonitoredRouteAPI = useMonitoredRouteAPI();

  const getInitialDirections = async (): Promise<RouteDirections> => {
    try {
      setIsBusy(true);

      if (!sessionUser) {
        return [];
      }

      const response = await MonitoredRouteAPI.lastData({
        customerId: sessionUser.customer_id
      });

      return await buildDirections(response.data);
    } finally {
      setIsBusy(false);
    }
  };

  const getDirections = (): RouteDirections => {
    if (routeDirectionsState.length > 0) {
      return [...routeDirectionsState];
    }

    return [];
  };

  const buildDirections = async (
    routes: RouteData[]
  ): Promise<RouteDirections> => {
    try {
      setIsBusy(true);

      const routeDirectionsSaved = getDirections();

      /*
       * Mantém somente rotas que ainda existem.
       */
      const routeDirections = routeDirectionsSaved.filter(saved =>
        routeExists(saved.route, routes)
      );

      const savedRoutes: RouteData[] = flatMap(routeDirections, "route");

      const directionsToBuild = routes.filter(
        route => !routeExists(route, savedRoutes)
      );

      /*
       * Atualiza os dados das rotas que já possuem
       * direção calculada.
       */
      for (const route of routes) {
        const existing = routeDirections.find(rd =>
          routeIsEqual(rd.route, route)
        );

        if (existing) {
          existing.route = route;
        }
      }

      /*
       * Traçado reto entre as duas câmeras: não segue as vias, mas
       * dispensa a Routes API (cobrada por requisição) e não pode falhar.
       */
      for (const route of directionsToBuild) {
        try {
          routeDirections.push({
            route,
            path: [
              getLatLngFromPosition(route.initial_position),
              getLatLngFromPosition(route.final_position)
            ]
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(
            `Invalid coordinates for route ${route.route_name}:`,
            error
          );
        }
      }

      setRouteDirectionsState(routeDirections);

      return routeDirections;
    } finally {
      setIsBusy(false);
    }
  };

  const updateDirectionsState = (routeDirections: RouteDirections) => {
    setRouteDirectionsState(routeDirections);
  };

  return [isBusy, getInitialDirections, buildDirections, updateDirectionsState];
};

export default useRouteFlowManager;
